import { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router-dom';
import { ErrorComponent } from '../components/error/ErrorComponent';
import { HomePage } from '../pages/home';
import { ProfileLayoutSkeleton } from '../pages/profile/layout/ProfileLayoutSkeleton';
import { AchievementPageSkeleton } from '../pages/profile/pages/achievement/AchievementPageSkeleton';
import { EditAchievementPageSkeleton } from '../pages/profile/pages/edit-achievement/EditAchievementPageSkeleton';
import { EditExperiencePageSkeleton } from '../pages/profile/pages/edit-experience/EditExperiencePageSkeleton';
import { EditFamilyPageSkeleton } from '../pages/profile/pages/edit-family/EditFamilyPageSkeleton';
import { EditSkillPageSkeleton } from '../pages/profile/pages/edit-skill/EditSkillPageSkeleton';
import { ExperiencePageSkeleton } from '../pages/profile/pages/experience/ExperiencePageSkeleton';
import { FamilyPageSkeleton } from '../pages/profile/pages/family/FamilyPageSkeleton';
import { ResetPasswordPageSkeleton } from '../pages/profile/pages/reset-password/ResetPasswordPageSkeleton';
import { EducationPageSkeleton } from '../pages/profile/pages/education/EducationPageSkeleton';
import { EditEducationPageSkeleton } from '../pages/profile/pages/edit-education/EditEducationPageSkeleton';
import { ProfilePageSkeleton } from '../pages/profile/ProfilePageSkeleton';
import { SignInPageSkeleton } from '../pages/signin/SigninPageSkeleton';
import { SignUpPageSkeleton } from '../pages/signup/SignupPageSkeleton';
import { VerificationPage } from '../pages/verification';
import { ForgotPasswordPageSkeleton } from '../pages/forgot-password/ForgotPasswordPageSkeleton';
import { APP_URLS } from './urls';
import { InstituteListPageSkeleton } from '../pages/institutes/InstituteListPageSkeleton';
import { ScholarshipListPageSkeleton } from '../pages/scholarships/ScholarshipListPageSkeleton';
import { ResetForgotPasswordPageSkeleton } from '../pages/reset-forgot-password/ResetForgotPasswordPageSkeleton';
import { InstituteDetailPageSkeleton } from '../pages/institutes/pages/detail/components/InstituteDetailPageSkeleton';
import { ScholarshipDetailPageSkeleton } from '../pages/scholarships/pages/detail/components/ScholarshipDetailPageSkeleton';
import { ProgramListPageSkeleton } from '../pages/programs/ProgramListPageSkeleton';
import { WishlistPageSkeleton } from '../pages/profile/pages/wishlist/WishlistPageSkeleton';
import { DocumentPageSkeleton } from '../pages/profile/pages/document/DocumentPageSkeleton';
import { EditDocumentPageSkeleton } from '../pages/profile/pages/edit-document/EditDocumentPageSkeleton';
import { ProgramDetailPageSkeleton } from '../pages/programs/pages/detail/components/ProgramDetailPageSkeleton';
import { EditProfilePageSkeleton } from '../pages/profile/pages/edit-profile/EditProfilePageSkeleton';
import { AdminRegistrationPageSkeleton } from '../pages/register-admin/components/AdminRegistrationPageSkeleton';
import { CourseDetailPageSkeleton } from '../pages/course/CourseDetailPageSkeleton';
import { RegisterAdminSuccessSkeleton } from '../pages/register-admin/pages/register-admin-success/RegisterAdminSuccessSkeleton';
import { MassApplyPageSkeleton } from '../pages/profile/shared/MassApplyPageSkeleton';
import { ApplicationPageSkeleton } from '../pages/profile/pages/application/ApplicationPageSkeleton';
import { EligibleModulesSkeleton } from '../pages/eligible-modules/EligibleModulesSkeleton';
import { EligibleModuleListSkeleton } from '../pages/eligible-modules/pages/EligibleModuleListSkeleton';
import { PageSpinner } from '../components/page-spinner/PageSpinner';
import { NewsListPageSkeleton } from '../pages/news/NewsListPageSkeleton';
import { NewsDetailPageSkeleton } from '../pages/news/pages/detail/NewsDetailPageSkeleton';
import { RoutineListPageSkeleton } from '../pages/routines/RoutineListPageSkeleton';
import { RoutineDetailPageSkeleton } from '../pages/routines/pages/detail/RoutineDetailPageSkeleton';
import InquiryPage from '../pages/inquiry';
import { NoticeListPageSkeleton } from '../pages/notices/NoticeListPageSkeleton';
import { NoticeDetailPageSkeleton } from '../pages/notices/pages/detail/NoticeDetailPageSkeleton';

/**
 * Auth Pages
 */
const SignInPage = lazy(() => import('../pages/signin'));
const SignUpPage = lazy(() => import('../pages/signup'));
const ForgotPasswordPage = lazy(() => import('../pages/forgot-password'));
const ResetForgotPasswordPage = lazy(() => import('../pages/reset-forgot-password'));
const AccountStatusPage = lazy(() => import('../pages/account-status'));

/**
 * Profile Pages
 */
const ProfileLayout = lazy(() => import('../pages/profile/layout/ProfileLayout'));
const ProfilePage = lazy(() => import('../pages/profile'));
const EditProfilePage = lazy(() => import('../pages/profile/pages/edit-profile'));
const FamilyPage = lazy(() => import('../pages/profile/pages/family'));
const EditFamilyPage = lazy(() => import('../pages/profile/pages/edit-family'));
const AchievementPage = lazy(() => import('../pages/profile/pages/achievement'));
const EditAchievementPage = lazy(() => import('../pages/profile/pages/edit-achievement'));
const ExperiencePage = lazy(() => import('../pages/profile/pages/experience'));
const EditExperiencePage = lazy(() => import('../pages/profile/pages/edit-experience'));
const EditSkillPage = lazy(() => import('../pages/profile/pages/edit-skill'));
const ResetPasswordPage = lazy(() => import('../pages/profile/pages/reset-password'));
const EducationPage = lazy(() => import('../pages/profile/pages/education'));
const EditEducationPage = lazy(() => import('../pages/profile/pages/edit-education'));
const WishlistPage = lazy(() => import('../pages/profile/pages/wishlist'));
const DocumentPage = lazy(() => import('../pages/profile/pages/document'));
const EditDocumentPage = lazy(() => import('../pages/profile/pages/edit-document'));
const ApplyProgramsPage = lazy(() => import('../pages/profile/pages/apply-programs'));
const ApplyScholarshipsPage = lazy(() => import('../pages/profile/pages/apply-scholarships'));
const ApplicationPage = lazy(() => import('../pages/profile/pages/application'));
const DeleteAccountPage = lazy(() => import('../pages/profile/pages/delete-account'));
const InquiryPageLayout = lazy(() => import('../pages/inquiry/InquiryPageLayout'));
const InquiryThreadPage = lazy(() => import('../pages/inquiry/thread'));

/**
 * Institute Pages
 */
const InstituteListPage = lazy(() => import('../pages/institutes'));
const InstituteDetailPage = lazy(() => import('../pages/institutes/pages/detail'));

/**
 * Scholarship Pages
 */
const ScholarshipListPage = lazy(() => import('../pages/scholarships'));
const ScholarshipDetailPage = lazy(() => import('../pages/scholarships/pages/detail'));

/**
 * Program Pages
 */
const ProgramListPage = lazy(() => import('../pages/programs'));
const ProgramDetailPage = lazy(() => import('../pages/programs/pages/detail'));

/**
 * Course Page
 */
const CourseDetailPage = lazy(() => import('../pages/course'));

/**
 * Admin Registration Pages
 */
const AcademicAdminRegistrationPage = lazy(() => import('../pages/register-admin/pages/academic-admin'));
const ScholarshipProviderRegistrationPage = lazy(() => import('../pages/register-admin/pages/scholarship-admin'));
const RegisterAdminSuccessPage = lazy(() => import('../pages/register-admin/pages/register-admin-success'));
const AdAdminRegistrationPage = lazy(() => import('../pages/register-admin/pages/ads-admin'));

/**
 * Eligible Modules Pages
 */
const EligibleModulesPage = lazy(() => import('../pages/eligible-modules'));
const EligibleInstitutesPage = lazy(() => import('../pages/eligible-modules/pages/eligible-institutes'));
const EligibleProgramsPage = lazy(() => import('../pages/eligible-modules/pages/eligible-programs'));
const EligibleScholarshipsPage = lazy(() => import('../pages/eligible-modules/pages/eligible-scholarships'));

/**
 * News Pages
 */
const NewsListPage = lazy(() => import('../pages/news'));
const NewsDetailPage = lazy(() => import('../pages/news/pages/detail'));

/**
 * Notices Pages
 */
const NoticeListPage = lazy(() => import('../pages/notices'));
const NoticeDetailPage = lazy(() => import('../pages/notices/pages/detail'));

/**
 * Routines Page
 */
const RoutinesListPage = lazy(() => import('../pages/routines'));
const RoutineDetailPage = lazy(() => import('../pages/routines/pages/detail'));

/**
 * Other Pages
 */
const ContactUsPage = lazy(() => import('../pages/contact'));
const PrivacyPolicyPage = lazy(() => import('../pages/privacy-policy'));
const TermsOfServicePage = lazy(() => import('../pages/terms-of-service'));

/**
 * Here we are using Suspense for each component.
 *
 * We are using code splitting pattern for each page
 * Suspense component here basically waits for those code to loads and provide fallback ui until it loads for better UX
 * This approach is taken to show loading component based on page users visit
 */
export const routes: RouteObject[] = [
  {
    path: APP_URLS.home,
    element: <HomePage />,
  },

  /**
   * Auth Related Pages
   */
  {
    path: APP_URLS.signIn,
    element: (
      <Suspense fallback={<SignInPageSkeleton />}>
        <SignInPage />
      </Suspense>
    ),
  },
  {
    path: APP_URLS.signUp,
    element: (
      <Suspense fallback={<SignUpPageSkeleton />}>
        <SignUpPage />
      </Suspense>
    ),
  },
  {
    path: APP_URLS.verify,
    element: <VerificationPage />,
  },

  {
    path: APP_URLS.forgotPassword,
    element: (
      <Suspense fallback={<ForgotPasswordPageSkeleton />}>
        <ForgotPasswordPage />
      </Suspense>
    ),
  },
  {
    path: APP_URLS.resetPassword,
    element: (
      <Suspense fallback={<ResetForgotPasswordPageSkeleton />}>
        <ResetForgotPasswordPage />,
      </Suspense>
    ),
  },
  {
    path: APP_URLS.accountStatus,
    element: (
      <Suspense fallback={<PageSpinner />}>
        <AccountStatusPage />
      </Suspense>
    ),
  },

  /**
   * Student Profile Pages
   */
  {
    path: APP_URLS.profile,
    element: (
      <Suspense fallback={<ProfileLayoutSkeleton />}>
        <ProfileLayout />
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<ProfilePageSkeleton />}>
            <ProfilePage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.editProfile,
        element: (
          <Suspense fallback={<EditProfilePageSkeleton />}>
            <EditProfilePage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.editSkills,
        element: (
          <Suspense fallback={<EditSkillPageSkeleton />}>
            <EditSkillPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.editPassword,
        element: (
          <Suspense fallback={<ResetPasswordPageSkeleton />}>
            <ResetPasswordPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.familyInfo,
        element: (
          <Suspense fallback={<FamilyPageSkeleton />}>
            <FamilyPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.editFamilyInfo,
        element: (
          <Suspense fallback={<EditFamilyPageSkeleton />}>
            <EditFamilyPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.achievementInfo,
        element: (
          <Suspense fallback={<AchievementPageSkeleton />}>
            <AchievementPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.editAchievementInfo,
        element: (
          <Suspense fallback={<EditAchievementPageSkeleton />}>
            <EditAchievementPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.workInfo,
        element: (
          <Suspense fallback={<ExperiencePageSkeleton />}>
            <ExperiencePage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.editWorkInfo,
        element: (
          <Suspense fallback={<EditExperiencePageSkeleton />}>
            <EditExperiencePage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.educationInfo,
        element: (
          <Suspense fallback={<EducationPageSkeleton />}>
            <EducationPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.editEducationInfo,
        element: (
          <Suspense fallback={<EditEducationPageSkeleton />}>
            <EditEducationPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.wishlistInfo,
        element: (
          <Suspense fallback={<WishlistPageSkeleton />}>
            <WishlistPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.documentInfo,
        element: (
          <Suspense fallback={<DocumentPageSkeleton />}>
            <DocumentPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.editDocumentInfo,
        element: (
          <Suspense fallback={<EditDocumentPageSkeleton />}>
            <EditDocumentPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.applyToMultiplePrograms,
        element: (
          <Suspense fallback={<MassApplyPageSkeleton type="institute_programs" />}>
            <ApplyProgramsPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.applyToMultipleScholarships,
        element: (
          <Suspense fallback={<MassApplyPageSkeleton type="scholarships" />}>
            <ApplyScholarshipsPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.applicationInfo,
        element: (
          <Suspense fallback={<ApplicationPageSkeleton />}>
            <ApplicationPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.deleteAccount,
        element: (
          <Suspense fallback={<PageSpinner />}>
            <DeleteAccountPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: APP_URLS.inboxList,
    element: (
      <Suspense fallback={<PageSpinner />}>
        <InquiryPageLayout />
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: <InquiryPage />,
      },
      {
        path: APP_URLS.inboxDetail,
        element: (
          <Suspense fallback={<PageSpinner />}>
            <InquiryThreadPage />
          </Suspense>
        ),
      },
    ],
  },

  /**
   * Institutes Related Pages
   */
  {
    path: APP_URLS.institutes,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<InstituteListPageSkeleton />}>
            <InstituteListPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.singleInstitute,
        element: (
          <Suspense fallback={<InstituteDetailPageSkeleton />}>
            <InstituteDetailPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.singleProgram,
        element: (
          <Suspense fallback={<ProgramDetailPageSkeleton />}>
            <ProgramDetailPage />
          </Suspense>
        ),
      },
    ],
  },

  /**
   * Scholarship Related Pages
   */
  {
    path: APP_URLS.scholarships,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<ScholarshipListPageSkeleton />}>
            <ScholarshipListPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.singleScholarship,
        element: (
          <Suspense fallback={<ScholarshipDetailPageSkeleton />}>
            <ScholarshipDetailPage />
          </Suspense>
        ),
      },
    ],
  },

  /**
   * Programs Related Pages
   */
  {
    path: APP_URLS.programs,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<ProgramListPageSkeleton />}>
            <ProgramListPage />
          </Suspense>
        ),
      },
    ],
  },

  /**
   * Course Page
   */
  {
    path: APP_URLS.singleCourse,
    element: (
      <Suspense fallback={<CourseDetailPageSkeleton />}>
        <CourseDetailPage />
      </Suspense>
    ),
  },

  /**
   * Admin Registration Page
   */
  {
    path: APP_URLS.academicAdminRegister,
    element: (
      <Suspense fallback={<AdminRegistrationPageSkeleton />}>
        <AcademicAdminRegistrationPage />
      </Suspense>
    ),
  },
  {
    path: APP_URLS.scholarshipAdminRegister,
    element: (
      <Suspense fallback={<AdminRegistrationPageSkeleton />}>
        <ScholarshipProviderRegistrationPage />
      </Suspense>
    ),
  },
  {
    path: APP_URLS.adminRegisterSuccess,
    element: (
      <Suspense fallback={<RegisterAdminSuccessSkeleton />}>
        <RegisterAdminSuccessPage />
      </Suspense>
    ),
  },
  {
    path: APP_URLS.adAdminRegister,
    element: (
      <Suspense fallback={<PageSpinner />}>
        <AdAdminRegistrationPage />
      </Suspense>
    ),
  },

  /**
   * Eligible Modules Page
   */
  {
    path: APP_URLS.eligibleModules,
    element: (
      <Suspense fallback={<EligibleModulesSkeleton />}>
        <EligibleModulesPage />
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<EligibleModuleListSkeleton module="Institutes" />}>
            <EligibleInstitutesPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.eligibleInstitutes,
        element: (
          <Suspense fallback={<EligibleModuleListSkeleton module="Institutes" />}>
            <EligibleInstitutesPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.eligiblePrograms,
        element: (
          <Suspense fallback={<EligibleModuleListSkeleton module="Programs" />}>
            <EligibleProgramsPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.eligibleScholarships,
        element: (
          <Suspense fallback={<EligibleModuleListSkeleton module="Scholarships" />}>
            <EligibleScholarshipsPage />
          </Suspense>
        ),
      },
    ],
  },

  /**
   * News pages
   */
  {
    path: APP_URLS.news,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<NewsListPageSkeleton />}>
            <NewsListPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.singleNews,
        element: (
          <Suspense fallback={<NewsDetailPageSkeleton />}>
            <NewsDetailPage />
          </Suspense>
        ),
      },
    ],
  },

  /**
   * Notices pages
   */
  {
    path: APP_URLS.notices,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<NoticeListPageSkeleton />}>
            <NoticeListPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.singleNotice,
        element: (
          <Suspense fallback={<NoticeDetailPageSkeleton />}>
            <NoticeDetailPage />
          </Suspense>
        ),
      },
    ],
  },

  /**
   * Routines pages
   */
  {
    path: APP_URLS.routines,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<RoutineListPageSkeleton />}>
            <RoutinesListPage />
          </Suspense>
        ),
      },
      {
        path: APP_URLS.singleRoutine,
        element: (
          <Suspense fallback={<RoutineDetailPageSkeleton />}>
            <RoutineDetailPage />
          </Suspense>
        ),
      },
    ],
  },

  /**
   * Other Pages
   */
  {
    path: APP_URLS.contact,
    element: (
      <Suspense fallback={<PageSpinner />}>
        <ContactUsPage />
      </Suspense>
    ),
  },
  {
    path: APP_URLS.privacyPolicy,
    element: (
      <Suspense fallback={<PageSpinner />}>
        <PrivacyPolicyPage />
      </Suspense>
    ),
  },
  {
    path: APP_URLS.termsOfService,
    element: (
      <Suspense fallback={<PageSpinner />}>
        <TermsOfServicePage />
      </Suspense>
    ),
  },

  /**
   * Not Found
   */
  {
    path: '*',
    element: <ErrorComponent errorStatus={404} />,
  },
];
